import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { Component, ElementRef, EventEmitter, HostBinding, Input, Output, Renderer2 } from '@angular/core';
import {
  BuildEnergyState,
  customerPortalTelemetryStyles,
  DetailsSeries,
  InverterV2,
  isNotNullOrUndefined,
  NodeType,
  PowerFlowGraphMode,
  System,
  SystemEnergyState,
} from 'ngx-core-services'
import { format } from 'date-fns';
import { distinctUntilChanged, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { ECharts, EChartsOption } from 'echarts';
import { Store } from '@ngrx/store';
import { isEqual } from 'lodash';

import { CreateCustomBlockSizeType, ICustomBlock } from '../decorators';
import {
  ChartAttributes,
  adaptPowersToGraph,
  appBuildGenericDailyGraph,
  updateSelectedAndAxisLimits,
  attributeLabels,
  categoryLabels,
  chartPlantAttributes,
} from '../utils';
import {
  SystemsState,
  requestSystemDailyEnergies,
  requestSystemDailyPowers,
  selectAzzurroHubHasAnySolarSensor,
  selectedSystemMetadataNodesType,
  selectedSystemMonitorPref,
  selectedSystemStructure,
  selectedSystemTz,
  selectedSystemUid
} from '../../store';
import {
  dailyEnergyStateBySelectedSystem,
  dailyEnergyStatusBySelectedSystem,
  dailyPowerStateBySelectedSystem,
  dailyPowerStatusBySelectedSystem
} from '../../store/selectors/daily-state-overview.selectors';
import { CreateBlockReduxSupport, IReduxBlock } from '../../store/helpers/decorator';
import { exportToExcel } from '../utils/export-xlsx';

const getLabels = () => ({
  chargedEnergy: 'Daily charged energy',
  consumedEnergy: 'Daily consumed energy',
  energyDailyIrradiation: 'Daily irradiation',
  dischargedEnergy: 'Daily discharged energy',
  exportedEnergy: 'Daily exported energy',
  generatedEnergy: 'Daily generated energy',
  importedEnergy: 'Daily imported energy',
  loading: 'Loading ...',
  noDataForTheSelectedPeriod: 'No data for the selected period',
  title: 'Daily overview',
  ...attributeLabels,
  ...categoryLabels,
});
@CreateBlockReduxSupport()
@CreateCustomBlockSizeType({
  'breakpoint-900': 900,
  'breakpoint-700': 700,
})
@Component({
  selector: 'lib-daily-state-overview',
  templateUrl: './daily-state-overview.component.html',
  styleUrls: ['./daily-state-overview.component.scss'],
})
export class DailyStateOverviewComponent implements ICustomBlock, IReduxBlock {
  @Input() refresh$ = new Subject<void>();
  _refresh$ = new Observable<boolean>();

  @Input() locate = 'it-IT';

  readonly _labels = getLabels();
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }

  @Input() todayMode : boolean | null = false;
  @Input() rtZoomMode = false;
  @Input() showCalendarPicker = true;
  @Input() showPowerTitle = true;
  @Input() showEnergyOverview = true;

  private readonly zoomed$ = new BehaviorSubject<boolean>(false);
  private _zoomed = false;
  @Input() set zoomed(value: boolean) {
    this._zoomed = value;
    this.zoomed$.next(value);
  }
  get zoomed(): boolean { return this._zoomed; }

  @HostBinding('class.hide-power-title')
  get hidePowerTitleClass(): boolean {
    return !this.showPowerTitle;
  }

  get labels(): typeof this._labels {
    return this._labels;
  }

  readonly system$ = new BehaviorSubject<System|undefined>(undefined);
  @Input() system?: System;


  @Output() zoomedChange = new EventEmitter<boolean>();
  @Output() updateTelemetryParams = new EventEmitter<string>();

  readonly _instanceId$: Observable<string> = new Observable<string>();

  chartInstances: ECharts[] = [];

  readonly isAzzurroHub$ = this.store.select(selectedSystemMonitorPref).pipe(
    map((monitoringType) => monitoringType === 'AzzurroHub'),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly baseSourceOptions$ = this.store.select(
    selectedSystemMetadataNodesType
  ).pipe(
    isNotNullOrUndefined(),
    distinctUntilChanged((prev, curr) => {
      return isEqual(prev, curr);
    }),
    tap((nodesType) => {
      this.sourceSelection$.next(nodesType as NodeType[]);
    }),
  );


  readonly hasSolarSensor$ =  this.store.select(selectAzzurroHubHasAnySolarSensor)

  readonly sourceOptions$ = combineLatest([
    this.baseSourceOptions$,
    this.isAzzurroHub$,
    this.hasSolarSensor$,
  ]).pipe(
    map(([options, isAzzurroHub, hasSolarSensor]) => {
      if (isAzzurroHub && hasSolarSensor) {
        return [...options, 'irradiance', 'panelTemperature'] as NodeType[];
      }
      return options;
    }),
    tap((sourceOptions) => {
      if (sourceOptions.length) {
        this.sourceSelection$.next(sourceOptions);
      }
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly sourceSelection$ = new BehaviorSubject<NodeType[]>([]);
  readonly systemTz$ = this.store.select(selectedSystemTz).pipe(isNotNullOrUndefined());
  readonly dateSelection$ = new BehaviorSubject<string>(format(new Date(), 'yyyy-MM-dd'));
  readonly dailyPowerStatus$ = this._instanceId$.pipe(
    switchMap((instanceId) => this.store.select(dailyPowerStatusBySelectedSystem(instanceId)).pipe(isNotNullOrUndefined())),
  );
  readonly dailyEnergyStatus$ = this._instanceId$.pipe(
    switchMap((instanceId) => this.store.select(dailyEnergyStatusBySelectedSystem(instanceId)).pipe(isNotNullOrUndefined())),
  );

  _currentWidth = 0;
  set currentWidth(value: number) {
    if (value === this._currentWidth) {
      return;
    }

    this._currentWidth = value;
    this.chartInstances.forEach((chart) => chart.resize());
  }
  get currentWidth() {
    return this._currentWidth;
  }

  constructor(
    readonly store: Store<SystemsState>,
    readonly el: ElementRef,
    readonly renderer: Renderer2,
  ) { }

  readonly requestsIsPending$ = combineLatest([
    this.dailyPowerStatus$,
    this.dailyEnergyStatus$
  ]).pipe(
    map(([powerStatus, energyState]) => powerStatus === 'pending' || energyState === 'pending'),
  );

  readonly requestPowerAndEnergies$ = combineLatest([
    this.store.select(selectedSystemUid).pipe(isNotNullOrUndefined()),
    this.dateSelection$,
    this._refresh$,
    this._instanceId$,
  ]).pipe(
    tap(([systemId, date, forceRefresh, instanceId]) => {

      if (this.todayMode) {
        date = format(new Date(), 'yyyy-MM-dd');
      }

      const range = {
        from: date,
        to: date,
      }
      this.store.dispatch(requestSystemDailyEnergies({ systemId, instanceId, range, forceRefresh }));
      this.store.dispatch(requestSystemDailyPowers({ systemId, instanceId, range, forceRefresh }));
    }),
  );

  readonly energyData$ = this._instanceId$.pipe(
    switchMap((instanceId) => this.store.select(dailyEnergyStateBySelectedSystem(instanceId)).pipe(isNotNullOrUndefined())),
    isNotNullOrUndefined(),
    map((data) => BuildEnergyState(data)),
  );

  readonly energyGraphData$ = combineLatest([
    this.energyData$,
    this.store.select(selectedSystemStructure).pipe(isNotNullOrUndefined()),
    this.hasSolarSensor$,
  ]).pipe(
    map(([energyData, structure, hasSolarSensor]) => this.adaptEnergyToOverview(energyData, structure, hasSolarSensor)),
  );

  readonly showDataZoom$ = this.zoomed$;

  private readonly rawPowerGraph$ = combineLatest([
    this._instanceId$.pipe(
      switchMap((instanceId) => this.store.select(dailyPowerStateBySelectedSystem(instanceId)).pipe(isNotNullOrUndefined())),
    ),
    this.dateSelection$,
    this.sourceSelection$,
    this.sourceOptions$,
    this.systemTz$,
  ]).pipe(
    map(([powers, date, selectedSource, sourceOptions, tz]) => {
      return this.buildPowerGraph(`${tz}`, selectedSource, sourceOptions, date, powers);
    }),
  );

  readonly powerData$ = combineLatest([this.rawPowerGraph$, this.showDataZoom$]).pipe(
    map(([{ graph, hasValues }, showDataZoom]) => {
      if (!showDataZoom) {
        return {
          graph: { ...graph, dataZoom: [], grid: { ...(graph.grid || {}), bottom: 10 } },
          hasValues,
        };
      }
      return { graph, hasValues };
    }),
  );

  readonly pageData$ = combineLatest([
    this.energyGraphData$,
    this.powerData$,
  ]).pipe(
    map(([energyData, powerData]) => {
      return {
        energyData,
        powerData
      }
    }),
  );

  buildPowerGraph(
    tz: string,
    selectedSource: NodeType[],
    sourceOptions: NodeType[],
    date: string,
    powers: Partial<InverterV2>[],
  ) {
    const powerStateSeries = adaptPowersToGraph(powers, date, selectedSource, tz);

    // For solarimeter data (SOL_IRR, SOL_TEMP), 0 is a valid value, so we check if there are any data points
    // For power data, we check if any value is not zero
    const solarimeterSeries = ['SOL_IRR', 'SOL_TEMP'];
    const hasValues = powerStateSeries.some(({ name, values }) => {
      if (solarimeterSeries.includes(name)) {
        return values.length > 0;
      }
      return values.some(([_, value]) => value !== 0);
    });
    const chartAttributes: ChartAttributes[] = chartPlantAttributes.map((attribute) => {
      return ({
        id: attribute.id,
        attribute: attribute,
        index: 0,
      })
    });

    let dailyPowerState = appBuildGenericDailyGraph(powerStateSeries, chartAttributes, this.labels);

    if (this.rtZoomMode) {
      dailyPowerState = this.applyRtZoom(dailyPowerState, powerStateSeries);
    }

    const graph = updateSelectedAndAxisLimits(dailyPowerState, {
      P_DC_TOT: selectedSource.includes('solar'),
      P_EXP: selectedSource.includes('grid'),
      P_LOAD: selectedSource.includes('home'),
      P_B_AGGR: selectedSource.includes('battery'),
      SOL_IRR: selectedSource.includes('irradiance'),
      SOL_TEMP: selectedSource.includes('panelTemperature'),
    });

    return { graph, hasValues };
  }

  private applyRtZoom(options: EChartsOption, series: DetailsSeries[]): EChartsOption {
    // Find the max X value across all series (latest data point)
    let maxX = -Infinity;
    series.forEach((s) => {
      s.values.forEach(([x]) => {
        if (x > maxX) maxX = x;
      });
    });

    if (!isFinite(maxX)) {
      return options;
    }

    // RT window: 5 minutes before latest point, 30s padding after
    const RT_WINDOW_SECONDS = 5 * 60;
    const RT_PADDING_SECONDS = 30;
    const xMin = maxX - RT_WINDOW_SECONDS;
    const xMax = maxX + RT_PADDING_SECONDS;

    const formatHMS = (value: number) => {
      const totalSeconds = value + 12 * 3600;
      const h = Math.floor(totalSeconds / 3600) % 24;
      const m = Math.floor((totalSeconds % 3600) / 60);
      const s = Math.floor(totalSeconds % 60);
      return `${h < 10 ? '0' : ''}${h}:${m < 10 ? '0' : ''}${m}:${s < 10 ? '0' : ''}${s}`;
    };

    const existingTooltip = (options.tooltip as any) || {};
    const attributesMap = new Map(chartPlantAttributes.map(attr => [attr.id, attr]));
    const labels = this.labels;

    return {
      ...options,
      grid: {
        ...((options.grid as any) || {}),
        containLabel: true,
        bottom: 52,
      },
      tooltip: {
        ...existingTooltip,
        formatter: (elements: any): string => {
          const { data: [valuex] } = elements[0];
          const timeStr = formatHMS(valuex);

          const headerTemplate = elements.length > 0 ? `
            <div class="fx-row text-accent-700 fx-align-center fx-justify-between tw-text-sm p-1 bg-accent-100 radius-t-4">
              <div class="tw-font-sm ml-auto">${timeStr}</div>
            </div>
          ` : '';

          const detailsTemplate = elements.map((element: any) => {
            const { seriesName, data: [_, valuey], color } = element;
            const attr = attributesMap.get(seriesName);
            const useSignLabel = attr?.graphOpts.sign;
            const name = useSignLabel
              ? valuey > 0
                ? useSignLabel.positive
                : useSignLabel.negative
              : seriesName;
            const unit = attr?.graphOpts.unit || '';
            const value = useSignLabel ? Math.abs(valuey) : valuey;
            const label = labels[name as keyof typeof labels] || name;
            return `
            <div
              class="fx-row fx-align-center fx-justify-between tw-text-sm text-grey-400 p-1">
              <div class="tw-font-medium text-style-italic mr-5 fx-row">
                <div class="s-4 rounded mr-3" style="background: ${color}"></div>
                ${label}
              </div>
              <div class="tw-font-normal">${value} <span class="tw-text-xs tw-font-bold">${unit}</span></div>
            </div>`;
          }).join('');

          return `${headerTemplate}${detailsTemplate}`;
        },
      },
      xAxis: {
        ...(options.xAxis as any),
        min: xMin,
        max: xMax,
        maxInterval: 60,
        axisLabel: {
          ...((options.xAxis as any)?.axisLabel || {}),
          showMinLabel: true,
          showMaxLabel: true,
          margin: 14,
          formatter: (value: number) => formatHMS(value),
        },
      },
      yAxis: Array.isArray(options.yAxis)
        ? (options.yAxis as any[]).map((ax: any) => ({ ...ax, min: undefined, max: undefined }))
        : options.yAxis,
    };
  }

  adaptEnergyToOverview(data: SystemEnergyState, systemType: PowerFlowGraphMode, hasSolarSensor: boolean) {

    const generationEnergy = {
      name: 'generatedEnergy' as keyof typeof this.labels,
      bgColor: customerPortalTelemetryStyles('EG1').bgColor,
      value: Number(data.energyDailyGenerating || 0),
      unit: 'kWh',
    };

    const energyDailyIrradiation = {
      name: 'energyDailyIrradiation' as keyof typeof this.labels,
      bgColor: customerPortalTelemetryStyles('SOL_EG').bgColor,
      value: Number(data.energyDailyIrradiation),
      unit: 'kWh/m²',
    };

    const consumeEnergy = {
      name: 'consumedEnergy' as keyof typeof this.labels,
      bgColor: customerPortalTelemetryStyles('EG4').bgColor,
      value: Number(data.energyDailyConsuming || 0),
      unit: 'kWh',
    };

    const dischargedEnergy = {
      name: 'dischargedEnergy' as keyof typeof this.labels,
      bgColor: customerPortalTelemetryStyles('EG6').bgColor,
      value: Number(data.energyDailyDischarging || 0),
      unit: 'kWh',
    };

    const chargedEnergy = {
      name: 'chargedEnergy' as keyof typeof this.labels,
      bgColor: customerPortalTelemetryStyles('EG5').bgColor,
      value: Number(data.energyDailyCharging || 0),
      unit: 'kWh',
    };

    const importedEnergy = {
      name: 'importedEnergy' as keyof typeof this.labels,
      bgColor: customerPortalTelemetryStyles('EG3').bgColor,
      value: Number(data.energyDailyImporting || 0),
      unit: 'kWh',
    };

    const exportedEnergy = {
      name: 'exportedEnergy' as keyof typeof this.labels,
      bgColor: customerPortalTelemetryStyles('EG2').bgColor,
      value: Number(data.energyDailyExporting || 0),
      unit: 'kWh',
    };

    switch (systemType) {
      case PowerFlowGraphMode.OnlyGeneratedPower:
        return hasSolarSensor ? [generationEnergy, energyDailyIrradiation] : [generationEnergy];
      case PowerFlowGraphMode.WithImportExport:
        return hasSolarSensor
          ? [generationEnergy, energyDailyIrradiation, consumeEnergy, importedEnergy, exportedEnergy]
          : [generationEnergy, consumeEnergy, importedEnergy, exportedEnergy];
      case PowerFlowGraphMode.WithBattery:
        return hasSolarSensor
          ? [generationEnergy, energyDailyIrradiation, consumeEnergy, dischargedEnergy, chargedEnergy, importedEnergy, exportedEnergy]
          : [generationEnergy, consumeEnergy, dischargedEnergy, chargedEnergy, importedEnergy, exportedEnergy];
      default:
        return [];
    }
  }

  updateSourcesSelection(selectedItems: NodeType[] = this.sourceSelection$.value) {
    if (!selectedItems.length) {
      return;
    }
    this.sourceSelection$.next(selectedItems);
  }

  updateDateSelection() {
    const date = this.dateSelection$.value

    this.updateTelemetryParams.emit(date);
  }

  onChartInit(ec: ECharts) {
    this.chartInstances.push(ec);
  }

  onExport(series: any) {
    const timeTransformFn = (valuex: number) => {
      const hour = Math.floor((valuex + (12 * 60 * 60)) / ( 60 * 60));
      const minutes = Math.floor(((valuex + (12 * 60 * 60)) / 60) % 60);
      return `${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }

    const dataTransformFn = (name: string, value: any) => {
      return {
        name,
        value: `${Number(value).toFixed(2)} kW`,
      };
    }

    const date = this.dateSelection$.value;
    const filename = `plant_${date}.xlsx`;

    exportToExcel(series, this.labels, timeTransformFn, filename, date, dataTransformFn);
  }
}
